import { Injectable, ErrorHandler, InjectionToken, Inject } from '@angular/core'
import Rollbar from 'rollbar'
import { environment } from '../environments/environment'
import { Apollo } from 'apollo-angular'
import gql from 'graphql-tag'

const rollbarConfig: Rollbar.Configuration = {
  accessToken: environment.ROLLBAR_ACCESS_TOKEN,
  captureUncaught: false,
  captureUnhandledRejections: true,
  captureEmail: true,
  captureUsername: true,
  payload: {
    environment: environment.env,
    client: {
      javascript: {
        source_map_enabled: true, //this is now true by default
        code_version: environment.gitVersion || 'git version not supplied',
        // Optionally have Rollbar guess which frames the error was thrown from
        // when the browser does not provide line and column numbers.
        guess_uncaught_frames: true,
      }
    }
  },
  hostSafeList: [ window.location.hostname ],
  // return true to ignore the payload
  checkIgnore: (_isUncaught, _args, payload) => {
    let ignore = false

    const error: Error = payload?.['notifier']?.['diagnostic']?.['raw_error']
    if (error) {
      // network errors
      if (error.message.includes('NetworkError') ||
          error.message.includes('0 Unknown') ||
          error.stack?.includes('0 Unknown')) {
        ignore = true

      // browser extension errors
      } else if (error.stack.includes('chrome-extension') || error.stack.includes('safari-extension')) {
        ignore = true
      }
    }

    return ignore
  }
}

interface CurrentUser {
  id: number | string;
  email: string;
  fullName: string;
  adminImpersonating: boolean;
}

const currentUserQuery = gql`
  query CurrentUserQuery {
    currentUser {
      id
      email
      fullName
      adminImpersonating
    }
  }
`

export const RollbarService = new InjectionToken<Rollbar>('rollbar')

@Injectable()
export class RollbarErrorHandler extends ErrorHandler {
  constructor(@Inject(RollbarService) private rollbar: Rollbar, private apollo: Apollo) {
    super()
  }

  handleError(err: any): void {
    if (environment.production && !err.message.includes('IGNORE')) {
      const currentUser = this.getCurrentUser()
      this.rollbar.configure({
        payload: {
          person: {
            id: currentUser ? currentUser.id : null,
            username: currentUser ? currentUser.fullName : '',
            email: currentUser ? currentUser.email : ''
          }
        }
      })
      this.rollbar.error(err.originalError || err)
    } else {
      super.handleError(err)
    }
  }

  private getCurrentUser(): CurrentUser | undefined {
    let currentUser: CurrentUser
    try { // readQuery will error if user is not already loaded
      currentUser = this.apollo.getClient().readQuery<{ currentUser?: CurrentUser }>({
        query: currentUserQuery
      }).currentUser
    } catch {}
    if (currentUser && currentUser.adminImpersonating) {
      if (!currentUser.id.toString().startsWith('admin')) currentUser.id = 'admin-' + currentUser.id
      if (!currentUser.fullName.startsWith('[Admin]')) currentUser.fullName = '[Admin] ' + currentUser.fullName
    }
    return currentUser
  }
}


export function rollbarFactory(): Rollbar {
    return new Rollbar(rollbarConfig)
}
